import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { IColumnMappingTransformation } from 'app/model/dataflow/column-mapping-transformation-utilities';
import { BaseColumnMappingTransformation, IMappingResult } from './base-column-mapping-transformation';
import { ComplexTypeUtils } from 'app/authoring/component/transformations/complex-type-utils';
import { IDataFlowDataType, AnyDataFlowType, ArrayDataFlowType } from './dataflow-datatype';
import { ComplexDataFlowType } from './dataflow-complex-datatype';
import { DataFlowColumn, IBasedOnEntityRef } from '../dataset/dataflowcolumn';
import { ColumnReference } from '../dataset/columnReference';
import { DataFlowExpression } from './dataflowexpression';
import { ExpressionBasedColumn } from './expression-based.column';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { Owned } from '../base/named';
import { ExpressionBasedColumnPattern } from './expression-based.column.pattern';
import { ColumnExpressionProcessor, IProcessColumnResult } from './column-expression-processor';
import { DataFlowExpressionBasedMapColumn } from './dataflow-expression-based-map.column';
import { generateGrammarForColumns } from './dataflow-expression-utilities';
import { updateIdentifiers } from './dataflow-expression-update-identifiers';

@Metamodel.model({
    name: 'foldDown',
    display: [stringResources.DataFlowFoldDownTransformationLabel, stringResources.DataFlowFoldDownTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        },
        mapping: {
            validator: FoldDownTransformation.validateMappings
        }
    }
})
export class FoldDownTransformation extends BaseColumnMappingTransformation implements IColumnMappingTransformation {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        display: stringResources.FoldDownTransformationUnrollBy,
        constraint: {
            required: true,
            validator: FoldDownTransformation.validateUnroll
        }
    })
    public unrollBy: DataFlowExpression = this.createTemplatableExpression(AnyDataFlowType.TYPE, '');
    public unrollMultiple: Array<DataFlowExpression | ExpressionBasedColumnBase> = [];
    public columnPatterns: ExpressionBasedColumnBase[] = [];
    private matchedColumns: IProcessColumnResult[] = [];

    @Metamodel.attribute({
        display: stringResources.FoldDownTransformationUnrollBy,
        constraint: {
            validator: FoldDownTransformation.validateUnrollRoot
        }
    })
    public unrollRoot: DataFlowExpression = this.createTemplatableExpression(AnyDataFlowType.TYPE, '');

    public get inputColumnsOnReset(): ColumnReference[] {
        if ((this.unrollBy && this.unrollBy.templateExpression) || (this.unrollRoot && this.unrollRoot.templateExpression)) {
            return [];
        }
        const unrollByValue = this.unrollBy?.expression;
        const unrollRootColumn = FoldDownTransformation.getPathBaseColumn(this, unrollByValue);
        if (this.unrollBy && unrollRootColumn) {
            const fieldColumnRefs = ComplexTypeUtils.getFieldsBetweenPaths(unrollRootColumn.column, this.unrollRoot.expression, unrollByValue).map((fieldPath) => {
                const columnRef = new ColumnReference(unrollRootColumn.column);
                columnRef.isAmbiguous = unrollRootColumn.isAmbiguous;
                columnRef.complexFieldPath = fieldPath;
                return columnRef;
            });
            const columnOptions: ColumnReference[] = [];
            if (!this.unrollRoot) {
                this.distinctInputColumnRefs.forEach((columnRef) => {
                    if (columnRef !== unrollRootColumn) {
                        columnOptions.push(columnRef);
                    } else {
                        columnOptions.push(...fieldColumnRefs);
                    }
                });
            } else {
                columnOptions.push(...fieldColumnRefs);
            }
            return columnOptions;
        }
        return this.distinctInputColumnRefs;
    }

    public populateMappingsOnEmptySchema: boolean = false;

    constructor() {
        super();
        this.unrollMultiple.push(this.unrollBy);
    }

    public static validateUnrollRoot(transformation: FoldDownTransformation, unrollValue: DataFlowExpression): IViolation[] {
        if ((transformation.unrollBy && transformation.unrollBy.templateExpression) || (unrollValue && unrollValue.templateExpression)) {
            return [];
        }
        const violations: IViolation[] = FoldDownTransformation.validateUnroll(transformation, unrollValue);
        if (unrollValue && !transformation.unrollBy?.expression.startsWith(unrollValue.expression)) {
            violations.push({
                context: transformation,
                message: () => sanitizeAndFormatString(stringResources.FoldDownUnrollRootIsInvalid, unrollValue.expression, transformation.unrollBy.expression)
            });
        }
        return violations;
    }

    public static validateUnroll(transformation: FoldDownTransformation, unrollValue: DataFlowExpression): IViolation[] {
        if (unrollValue?.templateExpression) {
            return [];
        }
        const violations: IViolation[] = [];
        if (unrollValue?.expression) {
            const value = unrollValue.expression;
            const unrollRootColumn = FoldDownTransformation.getPathBaseColumn(transformation, value);
            if (!unrollRootColumn) {
                violations.push({
                    context: transformation,
                    message: () => sanitizeAndFormatString(stringResources.FoldDownUnrollValuesInvalidColumnd, value)
                });
            } else {
                const columnType = ComplexTypeUtils.getFieldDataType(unrollRootColumn.column, value);
                if (columnType && columnType.typeString !== ArrayDataFlowType.TYPE) {
                    violations.push({
                        context: transformation,
                        message: () => sanitizeAndFormatString(stringResources.FoldDownUnrollValuesNotArray, value)
                    });
                }
            }
        }
        return violations;
    }

    public static validateMappings(transformation: FoldDownTransformation): IViolation[] {
        if (transformation.unrollBy?.templateExpression) {
            return [];
        }
        const violations: IViolation[] = [];
        transformation.mappingResults.forEach((mappingResult: IMappingResult) => {
            const mappingInputColumnType = ComplexTypeUtils.getFieldDataType(mappingResult.baseEntity.entity, mappingResult.baseEntity.name);
            if (mappingInputColumnType && mappingInputColumnType.typeString === ComplexDataFlowType.TYPE && transformation.unrollBy?.expression.startsWith(mappingResult.baseEntity.name)) {
                violations.push({
                    context: mappingResult.mapping,
                    message: () => sanitizeAndFormatString(stringResources.FoldDowMappingStructWithArray, mappingResult.baseEntity.name, mappingResult.outputColumn.column.name)
                });
            }
        });
        return violations;
    }

    private static getPathBaseColumn(transformation: FoldDownTransformation, path: string): ColumnReference {
        const fields = ComplexTypeUtils.splitComplexFieldPath(path);
        return transformation.distinctInputColumnRefs.find((col) => col.name === fields[0]);
    }

    public addColumn(index?: number): void {
        const emptyCol = this.createTemplatableExpression(AnyDataFlowType.TYPE, '');
        if (this.unrollBy?.expression) {
            this.unrollBy = emptyCol;
        }
        if (index === undefined || index === null) {
            this.unrollMultiple.push(emptyCol);
        } else {
            this.unrollMultiple.splice(index + 1, 0, emptyCol);
        }
    }

    public removeColumn(item: ExpressionBasedColumnBase | DataFlowExpression): void {
        const emptyCol = this.createTemplatableExpression(AnyDataFlowType.TYPE, '');
        const index = this.unrollMultiple.indexOf(item);
        if (index >= 0) {
            if (item instanceof ExpressionBasedColumnBase) {
                const expressionIndex = this.columnPatterns.indexOf(item);
                this.columnPatterns.splice(expressionIndex, 1);
            }
            const deletedCol = this.unrollMultiple.splice(index, 1);
            this.unrollBy = emptyCol;
            if (!this.unrollMultiple.length) {
                if (deletedCol[0] instanceof DataFlowExpression) {
                    this.addColumn();
                } else {
                    this.addColumnPattern();
                }
            } else if (this.unrollMultiple.length === 1 && this.unrollMultiple[0] instanceof DataFlowExpression) {
                this.unrollBy = this.unrollMultiple[0];
                this.unrollRoot = emptyCol;
            }
        }
    }

    public addColumnPattern(index?: number): ExpressionBasedColumnPattern {
        const columnPattern = Owned.create<ExpressionBasedColumnPattern>(ExpressionBasedColumnPattern.MODEL, this);
        this.columnPatterns.splice(index != null ? (index + 1) :  this.columnPatterns.length, 0, columnPattern);
        this.unrollMultiple.splice(index != null ? (index + 1) : this.unrollMultiple.length, 0, columnPattern);
        const expressionColumn = this.createExpressionBasedColumn(columnPattern);
        expressionColumn.dataFlowExpression = columnPattern.templateCondition;
        expressionColumn.nameOptional = true;
        expressionColumn.columnNameExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, expressionColumn);
        columnPattern.columns.splice(index != null ? (index + 1) : columnPattern.columns.length, 0, expressionColumn);
        return columnPattern;
    }

    public processPatterns(): void {
        this.matchedColumns = [];
        const generalArgs = generateGrammarForColumns(this.distinctInputColumnRefs, this.dataflow.cacheSinks, this.dataflow.parameters);
        this.columnPatterns.forEach((pattern) => updateIdentifiers(generalArgs, (pattern as ExpressionBasedColumnPattern).hierarchyLevelExpression));
        (this.columnPatterns as ExpressionBasedColumnPattern[]).forEach((columnPattern) => {
            const newExpressionBasedMapColumn = Owned.create<DataFlowExpressionBasedMapColumn>(DataFlowExpressionBasedMapColumn.MODEL, this);
            newExpressionBasedMapColumn.writeColumn = true;
            newExpressionBasedMapColumn.templateCondition = columnPattern.templateCondition;
            newExpressionBasedMapColumn.deepHierarchy = columnPattern.deepHierarchy;
            newExpressionBasedMapColumn.dataFlowExpression.expression = '$$';
            newExpressionBasedMapColumn.hierarchyLevelExpression = columnPattern.hierarchyLevelExpression;
            const mappingOutputColumns = ColumnExpressionProcessor.processNewColumns([newExpressionBasedMapColumn], this.distinctInputColumnRefs, this);
            this.matchedColumns.push(...mappingOutputColumns);
        });
    }

    protected getOutputColumnDataType(baseEntity: IBasedOnEntityRef): IDataFlowDataType {
        const idx = this.unrollMultiple.findIndex((col) => col instanceof DataFlowExpression && col.expression.startsWith(baseEntity.name));
        if (this.columnPatterns.length && !this.matchedColumns.length) {
            this.processPatterns();
        }
        const patternIdx = this.matchedColumns.findIndex((col) => col.baseEntity.name === baseEntity.name);
        return ComplexTypeUtils.getFieldDataType(baseEntity.rootEntity, baseEntity.name, idx !== -1 ? (this.unrollMultiple[idx] as DataFlowExpression).expression:
            patternIdx !== -1 ? this.matchedColumns[patternIdx].baseEntity.name : this.unrollBy?.expression);
    }

    protected getGeneratedDescription(): string {
        if (this.getFirstInputStream()) {
            return this.outputColumns().length > 0 ? format(stringResources.FoldDownDescription, this.unrollBy?.expression, this.unrollRoot ? ` ${stringResources.To.toLowerCase()} ${this.unrollRoot.expression}` : '',
                this.outputColumns().map((column) => column.name).join(', ')) : stringResources.FoldDownDescriptionNoColumns;
        }
        return stringResources.SelectDescriptionNoInputStream;
    }

    private createExpressionBasedColumn(columnPattern: ExpressionBasedColumnPattern): ExpressionBasedColumn {
        const column = Owned.create<ExpressionBasedColumn>(ExpressionBasedColumn.MODEL, this);
        column.column = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), columnPattern);
        return column;
    }
}
